import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, dateFormatter } from "@/lib/utils";
import { ILabel } from "@/types/label.types";
import { ColDef } from "ag-grid-community";
import { Circle, EllipsisVertical, Pencil } from "lucide-react";
import { PermissionValues } from "../role/constants";
import Trash from "@/components/icons/trash";
import { OrganizationMembership, WorkspaceMembership } from "@wacht/types";

export const createLabelTableColumns = (
  getMember: (id: string) => any | undefined,
  hasPermission: (permission: PermissionValues) => boolean,
  handleEditClick: (data: ILabel) => void,
  handleDeleteClick: (data: ILabel) => void,
): ColDef<ILabel>[] => [
  {
    field: "name",
    headerName: "Labels",
    minWidth: 250,
    cellRenderer: (params: { data: ILabel }) => {
      const color = params.data.color;

      return (
        <div className="flex items-center gap-4 h-full">
          <div className="flex items-center gap-2">
            <Badge
              variant="soft"
              className={cn("text-white dark:text-black max-w-60 shadow-none")}
              style={{
                backgroundColor: color,
              }}
            >
              <Circle className="h-2 w-2 fill-current mr-1" />
              <span className="truncate">{params.data.name}</span>
            </Badge>
          </div>
        </div>
      );
    },
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 300,
    cellRenderer: (params: { value: string }) => (
      <span className="text-sm">{params.value}</span>
    ),
  },
  {
    field: "createdAt",
    headerName: "Created On",
    minWidth: 200,
    cellRenderer: (params: { data: ILabel }) => (
      <span className="text-sm">{dateFormatter(params.data.createdAt)}</span>
    ),
  },
  {
    field: "createdBy",
    headerName: "Created By",
    minWidth: 250,
    cellRenderer: (params: { data: ILabel }) => {
      const member = getMember(params.data.createdBy);
      console.log("member", member,params.data);
      return (
        <div className="flex items-center gap-2">
          <Avatar className="w-5 h-5">
            <AvatarImage
              className="w-5 h-5"
              src={member?.public_user_data?.profile_picture_url as string || ""}
            />
          </Avatar>
          <span>
            {member?.public_user_data?.first_name} {member?.public_user_data?.last_name}
          </span>
        </div>
      );
    },
  },
  {
    colId: "actions",
    headerName: "Actions",
    maxWidth: 80,
    minWidth: 80,
    width: 80,
    filter: false,
    sortable: false,
    suppressMovable: true,
    pinned: "right",
    cellRenderer: (params: { data: ILabel }) => {
      const hasEditPermission = hasPermission("labels:edit");
      const hasDeletePermission = hasPermission("labels:delete");

      if (!hasEditPermission && !hasDeletePermission) return null;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <EllipsisVertical className="h-4 w-4 text-gray-600" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasEditPermission && (
              <DropdownMenuItem onClick={() => handleEditClick(params.data)}>
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            )}
            {hasDeletePermission && (
              <DropdownMenuItem
                onClick={() => handleDeleteClick(params.data)}
                className="delete-button group"
              >
                <Trash className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
